import { Injectable } from '@angular/core';
import { HttpService } from '../_shared/services/http.service';
import { environment } from '../../environments/environment';
import { ConfigitQuote, QuoteService } from '../_shared/services/quote.service';
import {
    PriceSummaryHelperService,
    ProductWithGroup,
} from '../_shared/components/price-summary/price-summary.helper.service';
import {
    OfferCreationResponse,
    Prices,
    PriceSummary,
    SetQuoteAsClosedParams,
} from '../_shared/components/price-summary/price-summary.model';
import { concatMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { of, throwError } from 'rxjs';
import { ProductList } from '../_shared/components/product-list/product-list.service';
import { isGroupWithHintText } from '../_shared/utils/offer.util';

@Injectable({
    providedIn: 'root',
})
export class SummaryDataService {
    constructor(
        private httpService: HttpService,
        private helperService: PriceSummaryHelperService,
        private translate: TranslateService,
        private quoteService: QuoteService
    ) {}

    public createOffer(
        customerId: string,
        quotationRequestId: string,
        voucherCode: string,
        productList: ProductList,
        lastPriceSummary: PriceSummary
    ) {
        const products: ProductWithGroup[] = productList.groups.flatMap((group) =>
            isGroupWithHintText(group)
                ? [
                      {
                          shortText: group.name,
                          printOptions: group.printOptions,
                          material: '',
                          selected: true,
                          quantity: 0,
                          group: undefined,
                      },
                  ]
                : group.products.map((product) => ({
                      ...product,
                      group: this.translate.instant(group.translationKey),
                  }))
        );
        const quote = this.quoteService.getCurrentQuote();
        const payload = this.helperService.getOfferCreationParams(
            customerId,
            voucherCode,
            products,
            productList.sampleOfferId,
            quote
        );
        payload.salesforceRequestId = quotationRequestId;
        const url = `${environment.http.quotation}offers`;
        return this.httpService.post<OfferCreationResponse>(url, payload).pipe(
            concatMap((response) => this.checkForErrors(response)),
            concatMap(({ documentNumber, expirationDate }) =>
                this.setQuoteAsClosed(quote.quote, {
                    sapDocumentNumber: documentNumber,
                    expirationDate,
                    customerId,
                    prices: <Prices>lastPriceSummary.prices,
                    voucherCode,
                })
            )
        );
    }

    private setQuoteAsClosed({ documentId, revision }: ConfigitQuote, payload: SetQuoteAsClosedParams) {
        const url = `${environment.http.baseUrl}quote/${documentId}/revision/${revision}/close`;
        return this.httpService.patch(url, payload);
    }

    private checkForErrors(response: OfferCreationResponse) {
        const errorMessage = response.messages.find((m) => m.type === 'ERROR');
        if (errorMessage) {
            return throwError(() => new Error(errorMessage.message));
        }
        return of(response);
    }

    public getAvailablePrintTypes(salesOrg: string) {
        const url = `${environment.http.baseUrl}quote/printTypes?salesOrg=${salesOrg}`;
        return this.httpService.get(url);
    }
}
